<!-- components/VDropdown.vue -->
<template>
    <div class="vd-dropdown" :class="[{ open: isOpen }, alignClass, positionClass]" ref="root">
        <!-- Trigger -->
        <button
            class="vd-btn"
            type="button"
            :aria-expanded="isOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            @click="toggle"
            @keydown.down.prevent="openAndFocus(0)"
            @keydown.enter.prevent="toggle"
            @keydown.space.prevent="toggle"
            :disabled="loading"
        >
            <slot name="label">{{ label }}</slot>
            <span v-if="loading" class="vd-loader"></span>
            <span v-else class="vd-caret">{{ caretSymbol }}</span>
        </button>

        <!-- Menu -->
        <ul
            v-show="isOpen"
            class="vd-menu"
            role="menu"
            :aria-label="menuAriaLabel"
            @keydown.stop.prevent="onMenuKeydown"
            ref="menu"
        >
            <li
                v-for="(item, i) in items"
                :key="item.key || item.value || i"
                class="vd-item"
                :class="[{ disabled: !!item.disabled || item.loading, active: i === focused }]"
                role="menuitem"
                tabindex="-1"
                @mouseenter="focused = i"
                @mouseleave="focused = -1"
                @click="onSelect(item, i, $event)"
            >
                <template v-if="item.href && !item.disabled">
                    <a class="vd-link" :href="item.href" :download="item.download || null" @click.stop>
                        <span v-if="item.icon" class="vd-ico" v-html="item.icon"></span>
                        {{ item.label }}
                    </a>
                </template>
                <template v-else>
                    <button class="vd-action" type="button" :disabled="!!item.disabled || item.loading">
                        <span v-if="item.loading" class="vd-item-loader"></span>
                        <span v-else-if="item.icon" class="vd-ico" v-html="item.icon"></span>
                        {{ item.label }}
                    </button>
                </template>
            </li>
            <li v-if="!items || items.length === 0" class="vd-empty" aria-disabled="true">
                <em>{{ emptyText }}</em>
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    name: 'VDropdown',
    props: {
        loading: { type: Boolean, default: false },
        label: { type: String, default: 'Exporter' },
        items: { type: Array, default: () => [] },
        align: { type: String, default: 'left' }, // 'left' | 'right'
        position: { type: String, default: 'bottom' }, // 'bottom' | 'top'
        autoFlip: { type: Boolean, default: true }, // détecte l’espace et inverse si besoin
        closeOnSelect: { type: Boolean, default: true },
        emptyText: { type: String, default: 'Aucun élément' },
        menuAriaLabel: { type: String, default: 'Menu' },
    },
    data() {
        return { isOpen: false, focused: -1, computedPosition: null } // null => utilise prop.position
    },
    computed: {
        alignClass() {
            return this.align === 'right' ? 'vd-right' : 'vd-left'
        },
        effectivePosition() {
            return this.computedPosition || this.position
        },
        positionClass() {
            return this.effectivePosition === 'top' ? 'vd-top' : 'vd-bottom'
        },
        caretSymbol() {
            // ▴ = flèche haut | ▾ = flèche bas
            return this.effectivePosition === 'top' ? '▴' : '▾'
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
        document.addEventListener('keydown', this.onGlobalKeydown)
        window.addEventListener('resize', this.onViewportChange)
        window.addEventListener('scroll', this.onViewportChange, true)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
        document.removeEventListener('keydown', this.onGlobalKeydown)
        window.removeEventListener('resize', this.onViewportChange)
        window.removeEventListener('scroll', this.onViewportChange, true)
    },
    methods: {
        toggle() {
            this.isOpen ? this.close() : this.open()
        },
        open() {
            if (this.isOpen) return
            this.isOpen = true
            this.$nextTick(() => {
                if (this.autoFlip) this.computeFlip()
                this.focusItem(0)
            })
            this.$emit('open')
        },
        close() {
            if (!this.isOpen) return
            this.isOpen = false
            this.focused = -1
            this.computedPosition = null // reset
            this.$emit('close')
        },
        openAndFocus(i) {
            this.open()
            this.$nextTick(() => this.focusItem(i))
        },
        handleClickOutside(e) {
            const root = this.$refs.root
            if (root && !root.contains(e.target)) this.close()
        },
        onGlobalKeydown(e) {
            if (!this.isOpen) return
            if (e.key === 'Escape') {
                e.preventDefault()
                this.close()
            }
        },
        onMenuKeydown(e) {
            const max = this.items.length - 1
            if (e.key === 'ArrowDown') this.focused = this.nextEnabledIndex(this.focused, +1)
            else if (e.key === 'ArrowUp') this.focused = this.nextEnabledIndex(this.focused, -1)
            else if (e.key === 'Home') this.focused = this.nextEnabledIndex(-1, +1)
            else if (e.key === 'End') this.focused = this.nextEnabledIndex(max + 1, -1)
            else if (e.key === 'Enter' || e.key === ' ') {
                const item = this.items[this.focused]
                if (item && !item.disabled) this.emitSelect(item, this.focused)
            }
        },
        focusItem(i) {
            this.focused = this.nextEnabledIndex(i - 1, +1)
        },
        nextEnabledIndex(start, step) {
            const n = this.items.length
            let i = start
            for (let k = 0; k < n; k++) {
                i = (i + step + n) % n
                if (!this.items[i]?.disabled) return i
            }
            return -1
        },
        onSelect(item, i) {
            if (!item.disabled && !item.loading) this.emitSelect(item, i)
        },
        emitSelect(item, i) {
            this.$emit('select', { item, index: i, value: item.value !== undefined ? item.value : item.label })
            if (this.closeOnSelect && !item.loading) this.close()
        },
        computeFlip() {
            const btn = this.$refs.root
            const menu = this.$refs.menu
            if (!btn || !menu) return

            const btnRect = btn.getBoundingClientRect()
            const menuH = menu.offsetHeight || 220 // fallback estimation
            const spaceBelow = window.innerHeight - btnRect.bottom
            const spaceAbove = btnRect.top

            // priorité: respecter prop.position si possible, sinon flip
            if (this.position === 'bottom') {
                this.computedPosition = spaceBelow >= menuH ? 'bottom' : spaceAbove > spaceBelow ? 'top' : 'bottom'
            } else {
                this.computedPosition = spaceAbove >= menuH ? 'top' : spaceBelow > spaceAbove ? 'bottom' : 'top'
            }
        },
        onViewportChange() {
            if (this.isOpen && this.autoFlip) this.$nextTick(this.computeFlip)
        },
    },
}
</script>

<style scoped>
.vd-dropdown {
    position: relative;
    display: inline-block;
}

/* Trigger */
.vd-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: clamp(12px, 0.729545vw, 16px);
    line-height: 1;
    padding: 0.5rem 0.75rem;
    border: 1px solid #7d2dbd;
    background: #fff;
    color: #7d2dbd;
    border-radius: 8px;
    cursor: pointer;
    height: 40px;
}
.vd-btn:hover {
    background: #ebf5ff;
}
.vd-caret {
    font-size: 12px;
    opacity: 0.8;
}

/* Menu */
.vd-menu {
    position: absolute;
    min-width: 230px;
    background: #fff;
    border: 1px solid #7d2dbd;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(56, 97, 251, 0.12);
    padding: 0.4rem 0;
    z-index: 1000;
}

/* Horizontal alignment */
.vd-left .vd-menu {
    left: -130px;
}
.vd-right .vd-menu {
    right: -84px;
}

/* Vertical alignment */
.vd-bottom .vd-menu {
    top: calc(100% + 6px);
}
.vd-top .vd-menu {
    bottom: calc(100% + -13px);
}

/* Items */
.vd-item {
    list-style: none;
}
.vd-link,
.vd-action {
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 0.55rem 1rem;
    font-size: clamp(12px, 0.729545vw, 16px);
    color: #7d2dbd;
    cursor: pointer;
}
.vd-item.active .vd-link,
.vd-item.active .vd-action {
    background: #f5f9ff;
}
.vd-item.disabled .vd-link,
.vd-item.disabled .vd-action {
    color: #94a3b8;
    cursor: not-allowed;
}
.vd-ico {
    margin-right: 0.5rem;
    display: inline-flex;
}
.vd-empty {
    padding: 0.55rem 1rem;
    color: #94a3b8;
}

.vd-loader,
.vd-item-loader {
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: vd-spin 0.8s linear infinite;
    display: inline-block;
}
.vd-loader {
    margin-left: 0.35rem;
}
.vd-item-loader {
    margin-right: 0.5rem;
}

@keyframes vd-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
